import { useCallback, useState } from "react";
import { toast } from "sonner";
import { Check, AlertTriangle, Clock } from "lucide-react";

import { Documentation } from "@/lib/api";

/**
 * 5-checkpoint documentation compliance strip.
 *
 * Regulatory deadlines:
 *   NURS - Nursing assessment (8h from admission)
 *   MTP  - Master Treatment Plan (72h from admission)
 *   PSY  - Psychosocial assessment (72h from admission)
 *   UPD  - MTP update (7 days after MTP or admission)
 *   DC   - Discharge summary (30 days after discharge)
 *
 * Visible-when-in-flight rule (per user's spec 2b): completed checkpoints
 * collapse to a subtle green check; only pending/due-soon/overdue chips
 * take real screen space.
 */
const EVENT_LABELS = {
  nursing_assessment: "NURS",
  mtp: "MTP",
  psychosocial: "PSY",
  mtp_updated: "UPD",
  discharge_summary: "DC",
};

const EVENT_FULL = {
  nursing_assessment: "Nursing Assessment (8h)",
  mtp: "Master Treatment Plan (72h)",
  psychosocial: "Psychosocial Assessment (72h)",
  mtp_updated: "MTP Update (7d)",
  discharge_summary: "Discharge Summary (30d)",
};

function statusStyles(status) {
  switch (status) {
    case "overdue":
      return "bg-[#fef2f2] text-[#b91c1c] border-[#fecaca] hover:bg-[#fee2e2]";
    case "due_soon":
      return "bg-[#fffbeb] text-[#92400e] border-[#fde68a] hover:bg-[#fef3c7]";
    case "on_time":
      return "bg-[#f0fdf4] text-[#166534] border-[#bbf7d0] hover:bg-[#dcfce7]";
    case "late":
      return "bg-[#fef2f2] text-[#b91c1c] border-[#fecaca] hover:bg-[#fee2e2]";
    case "pending":
      return "bg-[#f8fafc] text-[#475569] border-[#e2e8f0] hover:bg-[#f1f5f9]";
    default:
      return "bg-white text-[#94a3b8] border-[#e2e8f0]";
  }
}

function StatusIcon({ status }) {
  if (status === "overdue" || status === "late") return <AlertTriangle className="w-3 h-3" />;
  if (status === "due_soon") return <Clock className="w-3 h-3" />;
  if (status === "on_time") return <Check className="w-3 h-3" />;
  return null;
}

function formatHours(h) {
  if (h == null) return "";
  const sign = h < 0 ? "-" : "";
  const abs = Math.abs(h);
  if (abs < 1) return `${sign}${Math.round(abs * 60)}m`;
  if (abs < 48) return `${sign}${abs.toFixed(1)}h`;
  return `${sign}${(abs / 24).toFixed(1)}d`;
}

/**
 * DocumentationChips
 * @param {Object} props
 * @param {string} props.date - YYYY-MM-DD of the day this patient is on
 * @param {string} props.patientId
 * @param {Object} props.events - {event_key: {status, completed_at, completed_by, hours_remaining, due_at}}
 * @param {Function} props.onSaved - refetch parent after successful signoff
 * @param {boolean} [props.compact=false] - if true, hide the check-mark row for completed items entirely
 */
export default function DocumentationChips({ date, patientId, events, onSaved, compact = false }) {
  const [busy, setBusy] = useState(false);

  const signoff = useCallback(async (eventKey, currentlyCompleted) => {
    if (busy) return;
    if (currentlyCompleted) {
      // Second click on a completed chip → clear (undo)
      if (!window.confirm(`Clear the ${EVENT_FULL[eventKey]} signoff?`)) return;
      setBusy(true);
      try {
        await Documentation.signoff(date, patientId, eventKey, "", true);
        toast.success(`${EVENT_FULL[eventKey]} cleared`);
        onSaved?.();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Clear failed");
      } finally {
        setBusy(false);
      }
      return;
    }
    const initials = window.prompt(`Sign off ${EVENT_FULL[eventKey]} — enter your initials:`);
    if (!initials || !initials.trim()) return;
    setBusy(true);
    try {
      await Documentation.signoff(date, patientId, eventKey, initials.trim());
      toast.success(`${EVENT_FULL[eventKey]} signed by ${initials.trim().toUpperCase()}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signoff failed");
    } finally {
      setBusy(false);
    }
  }, [busy, date, patientId, onSaved]);

  const eventEntries = Object.entries(events || {});
  const inFlight = eventEntries.filter(([, s]) =>
    ["overdue", "due_soon", "pending"].includes(s.status)
  );
  const completed = eventEntries.filter(([, s]) =>
    ["on_time", "late"].includes(s.status)
  );

  return (
    <div
      className="flex items-center gap-1.5 flex-wrap"
      data-testid={`doc-chips-${patientId}`}
    >
      {inFlight.map(([evKey, s]) => (
        <button
          key={evKey}
          onClick={(e) => { e.stopPropagation(); signoff(evKey, false); }}
          className={[
            "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border transition-colors duration-200",
            statusStyles(s.status),
          ].join(" ")}
          data-testid={`doc-chip-${evKey}-${patientId}`}
          title={`${EVENT_FULL[evKey]} · ${s.status.replace("_", " ")}${s.hours_remaining != null ? ` · ${formatHours(s.hours_remaining)}${s.hours_remaining < 0 ? " overdue" : " remaining"}` : ""}`}
        >
          <StatusIcon status={s.status} />
          <span>{EVENT_LABELS[evKey]}</span>
          {s.hours_remaining != null && s.status !== "pending" && (
            <span className="opacity-70">{formatHours(s.hours_remaining)}</span>
          )}
        </button>
      ))}
      {!compact && completed.map(([evKey, s]) => (
        <button
          key={evKey}
          onClick={(e) => { e.stopPropagation(); signoff(evKey, true); }}
          className={[
            "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border transition-colors duration-200",
            statusStyles(s.status),
          ].join(" ")}
          data-testid={`doc-chip-${evKey}-${patientId}`}
          title={`${EVENT_FULL[evKey]} · signed by ${s.completed_by || "—"} · ${s.status.replace("_", " ")}`}
        >
          <StatusIcon status={s.status} />
          <span>{EVENT_LABELS[evKey]}</span>
        </button>
      ))}
      {inFlight.length === 0 && completed.length === 0 && (
        <span className="text-[10px] text-[#94a3b8] italic">no admission time</span>
      )}
    </div>
  );
}
